import { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDoc, doc } from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ListingCard from '../components/ListingCard';
import type { Listing, HomeAreaTab } from '@shared/types';
import {
  BROWSE_CITIES,
  getListingAreaGroupKey,
  getListingAreaGroupLabel,
  listingMatchesBrowseCity,
} from '../lib/locationRegions';
import { usePublishedListings } from '../hooks/usePublishedListings';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

const LOCATIONS = [...BROWSE_CITIES];

export default function Home() {
  const navigate = useNavigate();
  const [location, setLocation] = useState('');
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [guests, setGuests] = useState(2);
  const [enquiryName, setEnquiryName] = useState('');
  const [enquiryEmail, setEnquiryEmail] = useState('');
  const [enquiryPhone, setEnquiryPhone] = useState('');
  const [enquiryMessage, setEnquiryMessage] = useState('');
  const [sendingEnquiry, setSendingEnquiry] = useState(false);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (location) params.set('location', location);
    if (checkIn) params.set('checkIn', checkIn.toISOString().split('T')[0]);
    if (checkOut) params.set('checkOut', checkOut.toISOString().split('T')[0]);
    if (guests) params.set('guests', guests.toString());

    // If no filters selected, just open the generic browse page
    if (!location && !checkIn && !checkOut) {
      navigate('/search');
      return;
    }

    navigate(`/search?${params.toString()}`);
  };

  const handleHomeEnquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enquiryName || !enquiryEmail || !enquiryMessage) {
      toast.error('Please fill in your name, email, and message');
      return;
    }
    try {
      setSendingEnquiry(true);
      const fn = httpsCallable(functions, 'sendEnquiryEmailFunction');
      await fn({
        name: enquiryName,
        email: enquiryEmail,
        phone: enquiryPhone,
        message: enquiryMessage,
        listingId: 'general-enquiry',
      });
      toast.success('Enquiry sent. The team will contact you shortly.');
      setEnquiryMessage('');
    } catch (error: any) {
      toast.error(error?.message || 'Could not send enquiry. Please try again.');
    } finally {
      setSendingEnquiry(false);
    }
  };

  return (
    <div className="relative">
      {/* Hero Section with video background */}
      <div className="relative overflow-hidden">
        {/* Background video */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/hero.mp4"
          autoPlay
          loop
          muted
          playsInline
          aria-hidden="true"
        />
        {/* Light gradient overlay so video stays brighter; text still readable */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/35 via-[#32373c]/45 to-black/40" />
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-white">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-4 font-heading">Find Your Perfect Getaway</h1>
            <p className="text-xl mb-8 text-gray-200">
              Discover premium vacation rentals across South Africa
            </p>
          </div>

          {/* Search Form */}
          <div className="max-w-4xl mx-auto mt-12">
            <div className="bg-white rounded-lg shadow-xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <select
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="input"
                  >
                    <option value="">All locations</option>
                    {LOCATIONS.map((loc) => (
                      <option key={loc} value={loc}>
                        {loc}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check-in
                  </label>
                  <DatePicker
                    selected={checkIn}
                    onChange={(date) => setCheckIn(date)}
                    selectsStart
                    startDate={checkIn}
                    endDate={checkOut}
                    minDate={new Date()}
                    placeholderText="Check-in"
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check-out
                  </label>
                  <DatePicker
                    selected={checkOut}
                    onChange={(date) => setCheckOut(date)}
                    selectsEnd
                    startDate={checkIn}
                    endDate={checkOut}
                    minDate={checkIn || new Date()}
                    placeholderText="Check-out"
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Guests
                  </label>
                  <select
                    value={guests}
                    onChange={(e) => setGuests(parseInt(e.target.value) || 1)}
                    className="input"
                  >
                    {Array.from({ length: 8 }, (_, i) => i + 1).map((g) => (
                      <option key={g} value={g}>
                        {g} guest{g > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button onClick={handleSearch} className="btn btn-primary w-full mt-4 font-heading">
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Listings Section */}
      <FeaturedListings />

      {/* Browse by area */}
      <AreasSection />

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-4xl mb-4">🏖️</div>
            <h3 className="text-xl font-semibold mb-2 font-heading">Premium Properties</h3>
            <p className="text-gray-600 font-body">
              Hand-picked vacation rentals with exceptional amenities
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">🔒</div>
            <h3 className="text-xl font-semibold mb-2 font-heading">Secure booking</h3>
            <p className="text-gray-600 font-body">
              Request a stay online; we confirm and send payment details once approved
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">⭐</div>
            <h3 className="text-xl font-semibold mb-2 font-heading">Verified Reviews</h3>
            <p className="text-gray-600 font-body">
              Real reviews from verified guests to help you choose
            </p>
          </div>
        </div>
      </div>

      {/* General enquiry section */}
      <div className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 font-heading text-gray-900">
                Have a question about a stay?
              </h2>
              <p className="text-gray-600 mb-4">
                Send a quick enquiry and the Leisure Breaks team will help you find the right
                property or answer any questions about dates, pricing, or special requests.
              </p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-start">
                  <i className="uil uil-check-circle text-green-600 mr-2 mt-0.5"></i>
                  <span>Ask about multiple properties or future dates</span>
                </li>
                <li className="flex items-start">
                  <i className="uil uil-check-circle text-green-600 mr-2 mt-0.5"></i>
                  <span>Get help with group bookings or special occasions</span>
                </li>
                <li className="flex items-start">
                  <i className="uil uil-check-circle text-green-600 mr-2 mt-0.5"></i>
                  <span>Hear back directly from the Leisure Breaks team</span>
                </li>
              </ul>
            </div>
            <div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-7">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 font-heading flex items-center">
                  <i className="uil uil-envelope text-base mr-2 text-[#32373c]"></i>
                  Send an enquiry
                </h3>
                <form onSubmit={handleHomeEnquirySubmit} className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        className="input"
                        value={enquiryName}
                        onChange={(e) => setEnquiryName(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        className="input"
                        value={enquiryEmail}
                        onChange={(e) => setEnquiryEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      className="input"
                      value={enquiryPhone}
                      onChange={(e) => setEnquiryPhone(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <textarea
                      className="input"
                      rows={4}
                      value={enquiryMessage}
                      onChange={(e) => setEnquiryMessage(e.target.value)}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sendingEnquiry}
                    className="w-full py-3 px-4 bg-[#32373c] hover:bg-[#2a2e33] text-white font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
                  >
                    {sendingEnquiry ? (
                      <>
                        <i className="uil uil-spinner-alt animate-spin text-base mr-2"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="uil uil-envelope-send text-base mr-2"></i>
                        Send enquiry
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const AREA_INFO: Record<string, string> = {
  'Cape Town':
    'Coastal city with Table Mountain, beaches, and the Winelands nearby – ideal for city breaks and scenic getaways.',
  Durban:
    'Warm Indian Ocean beaches and a laid-back coastal vibe – perfect for family holidays year-round.',
  Johannesburg:
    'Urban hub with shopping, dining, and easy access to safari and Cradle of Humankind day trips.',
  Pretoria:
    'Jacaranda-lined streets and historic landmarks – a quieter base with plenty to explore.',
  'Port Elizabeth':
    'Friendly coastal city and gateway to the Garden Route – great for beach and outdoor lovers.',
};

function isAreaTabActiveToday(tab: HomeAreaTab): boolean {
  if (tab.enabled === false) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (tab.activeFrom && today.localeCompare(tab.activeFrom) < 0) return false;
  if (tab.activeUntil && today.localeCompare(tab.activeUntil) > 0) return false;
  return true;
}

function AreasSection() {
  const { data: homeConfig } = useQuery({
    queryKey: ['publicSettings', 'home'],
    queryFn: async () => {
      const snap = await getDoc(doc(db, 'publicSettings', 'home'));
      if (!snap.exists()) return {};
      return snap.data() as { homeAreas?: HomeAreaTab[] };
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: allListings, isLoading } = usePublishedListings();

  const dynamicCityTabs: HomeAreaTab[] = useMemo(() => {
    const src = allListings || [];
    const byCity = new Map<string, { label: string; count: number }>();
    for (const l of src) {
      const label = getListingAreaGroupLabel(l);
      const key = getListingAreaGroupKey(l);
      if (!key) continue;
      const prev = byCity.get(key);
      byCity.set(key, { label, count: (prev?.count || 0) + 1 });
    }

    const tabs = [...byCity.entries()]
      .sort((a, b) => b[1].count - a[1].count)
      .map(([key, v], i) => ({
        id: `dyn-${key}`,
        label: v.label,
        city: v.label,
        sortOrder: i,
      }));

    // Stable fallback order if there are few listings yet (keeps UX familiar)
    if (tabs.length === 0) {
      return LOCATIONS.map((city, i) => ({
        id: `default-${city}`,
        label: city,
        city,
        sortOrder: i,
      }));
    }
    return tabs;
  }, [allListings]);

  const areaTabs: HomeAreaTab[] = useMemo(() => {
    const raw = homeConfig?.homeAreas;
    if (raw && raw.length > 0) {
      return [...raw]
        .filter(isAreaTabActiveToday)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    return dynamicCityTabs;
  }, [homeConfig, dynamicCityTabs]);

  const [activeKey, setActiveKey] = useState<string>('');
  const activeTab = areaTabs.find((t) => t.id === activeKey) || areaTabs[0];

  useEffect(() => {
    if (areaTabs.length && !areaTabs.some((t) => t.id === activeKey)) {
      setActiveKey(areaTabs[0].id);
    }
  }, [areaTabs, activeKey]);

  const filtered = useMemo(() => {
    if (!allListings || !activeTab) return [];
    return allListings.filter((l) => listingMatchesBrowseCity(l, activeTab.city));
  }, [allListings, activeTab]);

  const description =
    activeTab && activeTab.description
      ? activeTab.description
      : AREA_INFO[activeTab?.city || ''] || `Properties in ${activeTab?.label || ''}.`;

  if (!areaTabs.length) {
    return (
      <div className="bg-white py-16 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold font-heading mb-2">Browse by area</h2>
          <p className="text-gray-600">
            No area tabs are active for today. An admin can enable areas and dates under Settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-16 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold font-heading mb-2">Browse by area</h2>
        <p className="text-gray-600 mb-8">Choose a destination to see properties in that region.</p>

        <div className="flex flex-wrap gap-2 mb-6">
          {areaTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveKey(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab?.id === tab.id
                  ? 'bg-[#32373c] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <p className="text-gray-700 mb-8 max-w-3xl">{description}</p>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#32373c]" />
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        ) : (
          <p className="text-gray-500">
            No published listings in {activeTab?.label || 'this area'} yet. Try another area or search.
          </p>
        )}

        {activeTab && (
          <div className="mt-8 text-center">
            <Link
              to={`/search?location=${encodeURIComponent(activeTab.city)}`}
              className="text-[#32373c] font-medium hover:underline"
            >
              View all in {activeTab.label} →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturedListings() {
  const { data: homeConfig } = useQuery({
    queryKey: ['publicSettings', 'home'],
    queryFn: async () => {
      const snap = await getDoc(doc(db, 'publicSettings', 'home'));
      if (!snap.exists()) return {};
      return snap.data() as { homeFeaturedListingIds?: string[] };
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: allListings, isLoading } = usePublishedListings();

  const listings = useMemo(() => {
    const src = allListings || [];
    if (!src.length) return [] as Listing[];

    const chosenIds = (homeConfig?.homeFeaturedListingIds || []).filter(Boolean);
    if (chosenIds.length > 0) {
      const byId = new Map(src.map((l) => [l.id, l]));
      const chosen = chosenIds
        .map((id) => byId.get(id))
        .filter((l): l is Listing => !!l && l.status === 'published')
        .slice(0, 12);
      if (chosen.length > 0) return chosen;
    }

    const featured = src.filter((l) => (l as Listing & { featured?: boolean }).featured);
    if (featured.length > 0) return featured.slice(0, 6);

    return [...src]
      .sort((a, b) => {
        const aTime =
          (a as any).createdAt?.toMillis?.() ||
          Date.parse((a as any).createdAt) ||
          0;
        const bTime =
          (b as any).createdAt?.toMillis?.() ||
          Date.parse((b as any).createdAt) ||
          0;
        return bTime - aTime;
      })
      .slice(0, 6);
  }, [allListings, homeConfig]);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c]"></div>
        </div>
      </div>
    );
  }

  if (!listings || listings.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold font-heading mb-2">Featured Properties</h2>
            <p className="text-gray-600 font-body">Discover our hand-picked selection</p>
          </div>
          <Link to="/search" className="text-[#32373c] hover:underline font-medium">
            View All →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((listing) => (
            <ListingCard key={listing.id} listing={listing} />
          ))}
        </div>
      </div>
    </div>
  );
}

